'use client'

import type { PaperProps } from '@mui/material'
import { Box, Paper } from '@mui/material'
import Typography from '@mui/material/Typography'
import { CSSProperties, PropsWithChildren, ReactElement } from 'react'
import { tss } from 'tss-react/mui'
import Button from '../Button/Button'
import MuiDivider from '../Divider/Divider'
import FlatList from '../FlatList/FlatList'
import { FlatListProps, ItemWithId } from '../FlatList/types'
import useMediaQueries from '../useMediaQueries'

export type FetchListProps<Item extends ItemWithId> = {
    /**
     * Whether the list should fetch more data when the user scrolls to the end
     */
    infiniteScrolling?: boolean
    /**
     * Set this true if there is more data to render
     */
    hasNextPage?: FlatListProps<Item>['hasMoreData']
    /**
     * Set this true while waiting for new data
     */
    isFetchingNextPage?: FlatListProps<Item>['isFetching']
    /**
     * Called when more data is required by the user's action, e.g. when the scroll position gets within scrollToEndThreshold of the rendered content
     */
    fetchNextPage?: FlatListProps<Item>['onScrollToEnd']
    /**
     * Add the paper component around the list, which is the most common case. Pass false when
     * the list is to be combined with other elements in the same box.
     */
    paper?: boolean
    /**
     * Styles to apply to the `<Paper>` component that will wrap the list
     */
    paperStyle?: PaperProps['style']
    /**
     * Styles to apply to the component that will wrap the list
     */
    listWrapperStyle?: CSSProperties
    /**
     * Show dividers between the items. Applies only for single-column lists.
     */
    dividers?: boolean
    /**
     * Apply tighter vertical spacing on desktop. Used for narrow lists with smaller teasers, e.g. member list in a sidebar.
     * Applies only for single-column lists.
     */
    dense?: boolean
    /**
     * An alternative loading more text copy or a replacement element
     */
    loadingMore?: string | ReactElement
    /**
     * Allows the position of loading more component to the bottom or the top (when for example the list is inverted, eg. messages)
     */
    loadingMorePosition?: 'bottom' | 'top'
    /**
     * Can remove the Divider between button and list
     */
    showButtonDivider?: boolean
    /**
     * Can set the width of the button used for loading more
     */
    fullWidth?: boolean
    /**
     * Style to apply to the button component
     */
    variant?: 'outlined' | 'text'
    /**
     * Animate the list item when it is mounted or unmounted
     */
    collapsible?: boolean
} & Pick<FlatListProps<Item>, 'data' | 'renderItem' | 'columns' | 'ListFooterComponent' | 'scrollToEndThreshold' | 'sx'>

const useStyles = tss.withNestedSelectors<'dense' | 'isGrid' | 'showButtonDivider'>().create(({ theme, classes }) => ({
    paper: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),

        [theme.breakpoints.up('sm')]: {
            paddingTop: theme.spacing(3),
            paddingBottom: theme.spacing(3),

            [`&.${classes.dense}`]: {
                paddingTop: theme.spacing(2),
                paddingBottom: theme.spacing(2),
            },
        },
    },
    dense: {},
    isGrid: {},
    showButtonDivider: {},
    spacingTop: {
        marginTop: theme.spacing(2),

        [theme.breakpoints.up('sm')]: {
            marginTop: theme.spacing(3),

            [`&.${classes.dense}`]: {
                marginTop: theme.spacing(2),
            },
        },
    },
    spacingBottom: {
        marginBottom: theme.spacing(2),

        [theme.breakpoints.up('sm')]: {
            marginBottom: theme.spacing(3),

            [`&.${classes.dense}`]: {
                marginBottom: theme.spacing(2),
            },
        },
    },
    marginBottom: {
        marginBottom: theme.spacing(2),
    },
    buttonWrapper: {
        textAlign: 'center',
        marginBottom: 0,
        paddingTop: theme.spacing(2),

        [`&.${classes.showButtonDivider}`]: {
            paddingTop: 0,
        },

        [theme.breakpoints.up('sm')]: {
            marginBottom: theme.spacing(-1),

            [`&.${classes.dense}:not(.${classes.isGrid})`]: {
                marginBottom: 0,
            },
        },
    },
    loadingMore: {
        marginBottom: theme.spacing(2),
        textAlign: 'center',
    },
}))

type ListFooterProps<Item extends ItemWithId> = Pick<
    FetchListProps<Item>,
    | 'infiniteScrolling'
    | 'hasNextPage'
    | 'showButtonDivider'
    | 'dense'
    | 'isFetchingNextPage'
    | 'fetchNextPage'
    | 'fullWidth'
    | 'variant'
> & {
    ListFooterComponentProp: FetchListProps<Item>['ListFooterComponent']
    infiniteScrolling: boolean
    isGrid: boolean | null
}

function ListFooter<Item extends ItemWithId>({
    ListFooterComponentProp,
    infiniteScrolling,
    hasNextPage,
    showButtonDivider,
    dense,
    isGrid,
    isFetchingNextPage,
    fetchNextPage,
    variant,
    fullWidth,
}: ListFooterProps<Item>): ReactElement | null {
    const { classes, cx } = useStyles()

    if (ListFooterComponentProp) {
        return <ListFooterComponentProp />
    }

    if (infiniteScrolling || !hasNextPage) {
        return null
    }

    return (
        <>
            {showButtonDivider ? (
                <MuiDivider className={cx(classes.spacingTop, classes.marginBottom, dense && classes.dense)} />
            ) : null}
            <div
                className={cx(
                    classes.buttonWrapper,
                    dense && classes.dense,
                    isGrid && classes.isGrid,
                    showButtonDivider && classes.showButtonDivider
                )}
            >
                <Button
                    disabled={Boolean(isFetchingNextPage)}
                    onClick={fetchNextPage}
                    variant={variant}
                    fullWidth={fullWidth}
                >
                    Show more
                </Button>
            </div>
        </>
    )
}

type RequiredDenseProp<Item extends ItemWithId> = Required<Pick<FetchListProps<Item>, 'dense'>>

function Divider<Item extends ItemWithId>({ dense }: RequiredDenseProp<Item>): ReactElement | null {
    const { classes, cx } = useStyles()

    return <MuiDivider className={cx(classes.spacingTop, classes.spacingBottom, dense && classes.dense)} />
}

function Wrapper<Item extends ItemWithId>({
    paper,
    dense,
    paperStyle,
    sx,
    children,
}: PropsWithChildren<Pick<FetchListProps<Item>, 'paper' | 'paperStyle' | 'dense' | 'sx'>>): ReactElement | null {
    const { classes, cx } = useStyles()

    if (paper) {
        return (
            <Paper style={paperStyle} className={cx(classes.paper, dense && classes.dense)} sx={sx}>
                {children}
            </Paper>
        )
    }

    return <>{children}</>
}

function EmptySpace<Item extends ItemWithId>({ dense }: RequiredDenseProp<Item>): ReactElement | null {
    const { classes, cx } = useStyles()

    return <Box className={cx(classes.spacingTop, dense && classes.dense)} />
}

function FetchList<Item extends ItemWithId>({
    data,
    renderItem,
    columns: columnsProp = 1,
    infiniteScrolling = false,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    paper = true,
    dividers = true,
    showButtonDivider = true,
    fullWidth = false,
    variant = 'text',
    dense = false,
    loadingMore = 'Loading...',
    loadingMorePosition = 'bottom',
    ListFooterComponent: ListFooterComponentProp,
    paperStyle,
    listWrapperStyle,
    scrollToEndThreshold,
    collapsible,
    sx,
}: FetchListProps<Item>): ReactElement | null {
    const { classes } = useStyles()
    const { breakpointDependentValue } = useMediaQueries()
    const isGrid = breakpointDependentValue(false, columnsProp > 1, null)
    const columns = breakpointDependentValue(1, columnsProp, undefined)
    const ListFooterComponent = () => (
        <ListFooter
            ListFooterComponentProp={ListFooterComponentProp}
            infiniteScrolling={infiniteScrolling}
            hasNextPage={hasNextPage}
            showButtonDivider={showButtonDivider}
            dense={dense}
            isGrid={isGrid}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            variant={variant}
            fullWidth={fullWidth}
        />
    )
    const LoadingMoreComponent = () =>
        infiniteScrolling ? (
            <div className={classes.loadingMore}>
                {dividers ? <Divider dense /> : <EmptySpace dense={dense} />}
                {typeof loadingMore === 'string' ? <Typography variant="body1">{loadingMore}</Typography> : loadingMore}
            </div>
        ) : null
    const SeparatorComponent = () => {
        if (isGrid) {
            return null
        }

        if (!dividers) {
            // Use an empty box to add space between items
            return <EmptySpace dense={dense} />
        }

        return <Divider dense />
    }

    return (
        <Wrapper paper={paper} paperStyle={paperStyle} dense={dense} sx={paper ? sx : undefined}>
            <FlatList
                data={data}
                renderItem={renderItem}
                columns={columns}
                hasMoreData={hasNextPage}
                isFetching={isFetchingNextPage}
                onScrollToEnd={infiniteScrolling ? fetchNextPage : undefined}
                ListFooterComponent={ListFooterComponent}
                ItemSeparatorComponent={SeparatorComponent}
                LoadingMoreComponent={LoadingMoreComponent}
                loadingMorePosition={loadingMorePosition}
                scrollToEndThreshold={scrollToEndThreshold}
                listWrapperStyle={listWrapperStyle}
                collapsible={collapsible}
                sx={!paper ? sx : undefined}
            />
        </Wrapper>
    )
}

export default FetchList
